// audio.js - Lightweight Web Audio sound engine (no asset files required).
// All sounds are synthesized from oscillators / noise, so there are no downloads
// and nothing to bundle. The AudioContext is created lazily and must be resumed
// from a user gesture (we call initAudio() when a game starts).

let ctx = null;
let masterGain = null;
let sfxGain = null;
let noiseBuffer = null;

// Current toggle state; mirrored from the React options.
const settings = { sfx: true };

const ensureContext = () => {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();

  masterGain = ctx.createGain();
  masterGain.gain.value = 0.6;
  masterGain.connect(ctx.destination);

  sfxGain = ctx.createGain();
  sfxGain.gain.value = settings.sfx ? 0.9 : 0;
  sfxGain.connect(masterGain);

  return ctx;
};

// Create/resume the context. Safe to call repeatedly; call from a click/keypress.
export const initAudio = () => {
  const c = ensureContext();
  if (c && c.state === 'suspended') {
    c.resume();
  }
};

export const setAudioSettings = (next) => {
  if (typeof next.sfx === 'boolean') settings.sfx = next.sfx;
  if (sfxGain) sfxGain.gain.value = settings.sfx ? 0.9 : 0;
};

// Cached 1s mono white-noise buffer, reused by whoosh/fireball/rocket sounds.
const getNoiseBuffer = () => {
  if (noiseBuffer) return noiseBuffer;
  const len = Math.floor(ctx.sampleRate * 1.0);
  noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return noiseBuffer;
};

// One-shot tone with an exponential decay envelope, optionally pitch-sliding.
const tone = (freq, dur, type = 'sine', vol = 0.3, slideTo = null) => {
  if (!ctx || !settings.sfx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (slideTo) {
    osc.frequency.exponentialRampToValueAtTime(slideTo, now + dur);
  }
  g.gain.setValueAtTime(vol, now);
  g.gain.exponentialRampToValueAtTime(0.001, now + dur);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(now);
  osc.stop(now + dur);
};

// ---- Sustained rocket whoosh (plays for the whole rocket boost) ----
let rocketNodes = null;

const startRocket = () => {
  if (!ctx || !settings.sfx || rocketNodes) return; // already playing
  const now = ctx.currentTime;

  // Airy whoosh: looping noise through a band-pass.
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer();
  src.loop = true;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 950;
  bp.Q.value = 0.7;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.linearRampToValueAtTime(0.32, now + 0.18);
  src.connect(bp);
  bp.connect(g);
  g.connect(sfxGain);
  src.start(now);

  // Low rumble for thrust body.
  const osc = ctx.createOscillator();
  const og = ctx.createGain();
  osc.type = 'sawtooth';
  osc.frequency.value = 68;
  og.gain.setValueAtTime(0.0001, now);
  og.gain.linearRampToValueAtTime(0.18, now + 0.18);
  osc.connect(og);
  og.connect(sfxGain);
  osc.start(now);

  rocketNodes = { src, g, osc, og };
};

const stopRocket = () => {
  if (!ctx || !rocketNodes) return;
  const now = ctx.currentTime;
  const { src, g, osc, og } = rocketNodes;
  [g, og].forEach((node) => {
    node.gain.cancelScheduledValues(now);
    node.gain.setValueAtTime(node.gain.value, now);
    node.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  });
  try { src.stop(now + 0.25); } catch (e) { /* already stopped */ }
  try { osc.stop(now + 0.25); } catch (e) { /* already stopped */ }
  rocketNodes = null;
};

export const sfx = {
  // Soft, quiet thump on every bounce (lots of bouncing, so keep it gentle).
  jump: () => tone(170, 0.13, 'sine', 0.16, 70),

  // Springs are rarer and exciting — a soft upward boing.
  spring: () => tone(240, 0.22, 'triangle', 0.28, 1000),

  coin: () => {
    tone(880, 0.07, 'square', 0.32);
    setTimeout(() => tone(1320, 0.12, 'square', 0.32), 55);
  },

  // Fireball: a low whoosh tone layered with a falling noise burst.
  shoot: () => {
    if (!ctx || !settings.sfx) return;
    const now = ctx.currentTime;

    const src = ctx.createBufferSource();
    src.buffer = getNoiseBuffer();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200, now);
    lp.frequency.exponentialRampToValueAtTime(420, now + 0.25);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.32, now);
    ng.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    src.connect(lp);
    lp.connect(ng);
    ng.connect(sfxGain);
    src.start(now);
    src.stop(now + 0.3);

    tone(300, 0.27, 'sawtooth', 0.22, 90); // fiery body
  },

  kill: () => tone(200, 0.26, 'sawtooth', 0.45, 55),

  // Electric magnet hum: two close low saws beat against each other.
  magnet: () => {
    if (!ctx || !settings.sfx) return;
    const now = ctx.currentTime;
    const dur = 1.2;
    [118, 123].forEach((f) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.linearRampToValueAtTime(0.15, now + 0.08);
      g.gain.setValueAtTime(0.15, now + dur - 0.25);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      osc.connect(g);
      g.connect(sfxGain);
      osc.start(now);
      osc.stop(now + dur);
    });
  },

  // Metallic shield clang: inharmonic partials ringing out.
  shield: () => {
    if (!ctx || !settings.sfx) return;
    const now = ctx.currentTime;
    [524, 791, 1170, 1655, 2310].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = f;
      const vol = 0.13 / (i + 1);
      g.gain.setValueAtTime(vol, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
      osc.connect(g);
      g.connect(sfxGain);
      osc.start(now);
      osc.stop(now + 0.55);
    });
  },

  // Slow-mo: a descending "winding down" pitch sweep.
  slowmo: () => {
    if (!ctx || !settings.sfx) return;
    const now = ctx.currentTime;
    const dur = 0.7;
    [0, 8].forEach((detune) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.detune.value = detune;
      osc.frequency.setValueAtTime(700, now);
      osc.frequency.exponentialRampToValueAtTime(140, now + dur);
      g.gain.setValueAtTime(0.24, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      osc.connect(g);
      g.connect(sfxGain);
      osc.start(now);
      osc.stop(now + dur);
    });
  },

  // Generic pleasant chime for the remaining pickups (shooter, 2x coins).
  powerup: () => {
    tone(523, 0.1, 'triangle', 0.4);
    setTimeout(() => tone(659, 0.1, 'triangle', 0.4), 80);
    setTimeout(() => tone(784, 0.16, 'triangle', 0.4), 160);
  },

  gameover: () => {
    tone(440, 0.2, 'sawtooth', 0.4, 110);
    setTimeout(() => tone(220, 0.45, 'sawtooth', 0.4, 80), 160);
  },

  rocketStart: startRocket,
  rocketStop: stopRocket,
};
